
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Download, ExternalLink } from 'lucide-react';

interface WaveDocument {
  id: string;
  status: string;
  total: number;
  created_at: string;
  wave_estimate_id?: string;
  wave_invoice_id?: string;
  wave_sync_status?: string;
}

interface WaveDocumentViewerProps {
  documents: WaveDocument[];
  type: 'estimate' | 'invoice';
}

const WaveDocumentViewer: React.FC<WaveDocumentViewerProps> = ({
  documents,
  type
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      estimate: {
        pending: { color: 'bg-yellow-500', text: 'Pending' },
        approved: { color: 'bg-green-500', text: 'Approved' },
        invoiced: { color: 'bg-blue-500', text: 'Invoiced' },
      },
      invoice: {
        unpaid: { color: 'bg-red-500', text: 'Unpaid' },
        paid: { color: 'bg-green-500', text: 'Paid' },
        overdue: { color: 'bg-orange-500', text: 'Overdue' },
      }
    } as const;

    const typeConfig = statusConfig[type];
    
    // Type-safe lookup with fallback
    if (type === 'estimate') {
      const estimateStatus = status as keyof typeof statusConfig.estimate;
      const config = statusConfig.estimate[estimateStatus];
      if (config) {
        return <Badge className={config.color}>{config.text}</Badge>;
      }
    } else if (type === 'invoice') {
      const invoiceStatus = status as keyof typeof statusConfig.invoice;
      const config = statusConfig.invoice[invoiceStatus];
      if (config) {
        return <Badge className={config.color}>{config.text}</Badge>;
      }
    }
    
    // Fallback for unknown statuses
    return <Badge variant="secondary">{status}</Badge>;
  };

  const getSyncStatusBadge = (syncStatus?: string) => {
    if (!syncStatus) return null;
    
    switch (syncStatus) {
      case 'synced':
        return <Badge variant="outline" className="text-green-600 border-green-300">Synced</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-300">Syncing</Badge>;
      case 'failed':
        return <Badge variant="outline" className="text-red-600 border-red-300">Sync Failed</Badge>;
      default:
        return null;
    }
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No {type}s found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {documents.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium">
                {type === 'estimate' ? 'Estimate' : 'Invoice'} #{doc.id.slice(-8)}
              </span>
              {getStatusBadge(doc.status)}
              {getSyncStatusBadge(doc.wave_sync_status)}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">${doc.total.toFixed(2)}</span>
              {' • '}
              <span>{new Date(doc.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm">
              <Eye className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Download className="h-4 w-4" />
            </Button>
            {(doc.wave_estimate_id || doc.wave_invoice_id) && (
              <Button variant="ghost" size="sm">
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WaveDocumentViewer;
